import { useEffect, useState } from "react";
import { PageHeader, Empty } from "@/components/UIBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function Pool() {
  const [pool, setPool] = useState([]);
  const [bulk, setBulk] = useState("");

  const load = async () => {
    try {
      setPool(await api.listPool());
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    const numbers = bulk
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!numbers.length) return toast.error("No numbers");
    try {
      const created = await api.addPool(numbers);
      toast.success(`Added ${created.length} number(s)`);
      setBulk("");
      load();
    } catch (e) {
      toast.error("Failed to add");
    }
  };

  const remove = async (n) => {
    try {
      await api.deletePool(n);
      toast.success(`Removed ${n}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  return (
    <div data-testid="pool-page">
      <PageHeader
        title="Shadow Pool"
        subtitle={`// ${pool.length} numbers provisioned`}
        testid="pool-header"
      />

      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div>
          {pool.length === 0 ? (
            <Empty testid="pool-empty">No shadow numbers yet — add some →</Empty>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {pool.map((p) => (
                <div
                  key={p.id}
                  className="border border-[#1f1f23] bg-[#0e0e10] p-4 flex items-start justify-between"
                  data-testid={`pool-card-${p.number}`}
                >
                  <div>
                    <p className="font-mono text-base text-white">{p.number}</p>
                    <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
                      {p.in_use_count > 0
                        ? `${p.in_use_count} active`
                        : "available"}
                    </p>
                  </div>
                  <button
                    onClick={() => remove(p.number)}
                    data-testid={`pool-remove-${p.number}`}
                    className="text-zinc-600 hover:text-red-400 transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="border border-[#1f1f23] bg-[#0e0e10] p-5">
          <h3 className="font-heading text-lg mb-1">Provision Numbers</h3>
          <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest mb-4">
            One per line or comma-separated
          </p>
          <Textarea
            data-testid="pool-bulk-input"
            value={bulk}
            onChange={(e) => setBulk(e.target.value)}
            placeholder={"+15551110000\n+15551110001\n+15551110002"}
            className="font-mono text-sm bg-black border-[#27272a] min-h-[160px]"
          />
          <Button
            onClick={add}
            data-testid="pool-add-btn"
            className="w-full mt-4 bg-white text-black hover:bg-zinc-200"
          >
            Add to Pool
          </Button>
          <p className="font-mono text-[10px] text-zinc-600 mt-4 leading-relaxed">
            Tip: Shadow numbers are multiplexed across users — the same number
            can serve different strangers for different users without collision.
          </p>
        </aside>
      </div>
    </div>
  );
}
